import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getSingleCalculation } from "../../../services/enduser/calculatorService";
import { printCalculationDocument } from "../../../utils/printCalculationDocument";
import TaxYearNotice from "../../shared/TaxYearNotice";
import { resolveSavedTaxYearNotice } from "../../../config/taxYearNotice";
import {
  extractM1VehicleData,
  extractM1CommercialData,
  extractN1ElectricPrivateData,
  extractN1TenderData,
  extractM1ElectricPrivateData,
  extractM1ElectricCommercialData,
  extractTaxiData,
  extractTaxiCommercialData,
  extractJetskiData,
  extractJetskiCommercialData,
  extractCollectorVehicleData,
  extractMotorcycleData,
  extractMotorcycleCommercialData,
  extractN1N2ElectricCommercialData,
  extractN1N2CommercialData,
  SAFETY_SYSTEMS_M1,
  SAFETY_SYSTEMS_MOTORCYCLE,
  getActiveSafetySystems,
  formatNumber,
  calculateTotalTaxes,
  calculateTaxRate,
  CalculationData,
  extractTrailerData,
} from "./calculatorConfigs";

const CalculationResultViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // State
  const [calculation, setCalculation] = useState<CalculationData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // ref לאזור התוצאה — מקור לשכפול בהדפסה החדשה (גרסת פיתוח)
  const printRef = useRef<HTMLDivElement>(null);

  // טעינת החישוב בעת mount
  useEffect(() => {
    const loadCalculation = async () => {
      if (!id) {
        setError("מזהה חישוב לא תקין");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError("");

        const result = await getSingleCalculation(parseInt(id));

        if (result.res === "OK") {
          setCalculation(result.data);
        } else {
          setError(result.msg || "שגיאה בטעינת החישוב");
        }
      } catch (err: any) {
        console.error("Error loading calculation:", err);
        setError(err.message || "שגיאה בטעינת החישוב");
      } finally {
        setIsLoading(false);
      }
    };

    loadCalculation();
  }, [id]);

  // פונקציה לחזרה להיסטוריה
  const handleGoBack = () => {
    navigate("/enduser/history");
  };

  // פונקציה להדפסה
  // const handlePrint = () => {
  //   window.print();
  // };

  // פונקציה להדפסה — מסמך נקי בעיצוב ייעודי דרך iframe (לא window.print על ה-DOM)
  const handlePrint = () => {
    printCalculationDocument(printRef.current, {
      calculatorName: data?.meta?.calculatorName,
      calculationDate: data?.meta?.calculationDate,
    });
  };
  // חילוץ נתונים לפי סוג מחשבון
  const getData = () => {
    if (!calculation) return null;

    const calcType = calculation.calculation_type;

    // M1 בנזין/דיזל - אישי
    if (calcType === "m1_vehicle") {
      return extractM1VehicleData(calculation);
    }

    // M1 בנזין/דיזל/היברידי - מסחרי
    if (calcType === "M1_vehicle_commercial" || calcType === "m1_commercial") {
      return extractM1CommercialData(calculation);
    }

    // M1 חשמלי - מסחרי
    if (
      calcType === "M1_electric_commercial" ||
      calcType === "m1_electric_commercial"
    ) {
      return extractM1ElectricCommercialData(calculation);
    }

    // M1 חשמלי - אישי
    if (
      calcType === "M1_electric_private_import" ||
      calcType === "m1_electric_private"
    ) {
      return extractM1ElectricPrivateData(calculation);
    }

    // N1 טנדר - אישי
    if (calcType === "n1_tender") {
      return extractN1TenderData(calculation);
    }

    // N1 חשמלי - אישי
    if (
      calcType === "N1_electric_private_import" ||
      calcType === "n1_electric_private"
    ) {
      return extractN1ElectricPrivateData(calculation);
    }

    // מוניות - אישי
    if (calcType === "taxi_vehicle" || calcType === "taxi") {
      return extractTaxiData(calculation);
    }

    // מוניות - מסחרי
    if (
      calcType === "taxi_vehicle_commercial" ||
      calcType === "taxi_commercial"
    ) {
      return extractTaxiCommercialData(calculation);
    }

    // רכב אספנות
    if (calcType === "collector_vehicle") {
      return extractCollectorVehicleData(calculation);
    }

    // אופנוע ים - אישי
    if (calcType === "jet_ski_vehicle" || calcType === "jetski") {
      return extractJetskiData(calculation);
    }

    // אופנוע ים - מסחרי
    if (calcType === "jet_ski_commercial" || calcType === "jetski_commercial") {
      return extractJetskiCommercialData(calculation);
    }

    // אופנועים - אישי
    if (calcType === "motorcycle_vehicle" || calcType === "motorcycle") {
      return extractMotorcycleData(calculation);
    }

    // אופנועים - מסחרי
    if (calcType === "motorcycle_commercial") {
      return extractMotorcycleCommercialData(calculation);
    }

    // N1 & N2 בנזין/דיזל - מסחרי
    if (calcType === "N1N2_commercial" || calcType === "n1n2_commercial") {
      return extractN1N2CommercialData(calculation);
    }

    // N1 & N2 חשמלי - מסחרי
    if (
      calcType === "N1_N2_electric_commercial" ||
      calcType === "n1_n2_electric_commercial"
    ) {
      return extractN1N2ElectricCommercialData(calculation);
    }

    // נגררים
    if (calcType === "trailer_vehicle" || calcType === "trailer") {
      return extractTrailerData(calculation);
    }

    // Fallback
    return extractM1VehicleData(calculation);
  };

  const data: any = getData();

  // בדיקה אם זה מחשבון מוניות
  const isTaxi =
    calculation?.calculation_type === "taxi_vehicle" ||
    calculation?.calculation_type === "taxi";

  // בדיקה אם זה מחשבון מוניות מסחרי
  const isTaxiCommercial =
    calculation?.calculation_type === "taxi_vehicle_commercial" ||
    calculation?.calculation_type === "taxi_commercial";

  // בדיקה אם זה מחשבון אופנוע ים
  const isJetski =
    calculation?.calculation_type === "jet_ski_vehicle" ||
    calculation?.calculation_type === "jetski";

  // בדיקה אם זה מחשבון אופנוע ים מסחרי
  const isJetskiCommercial =
    calculation?.calculation_type === "jet_ski_commercial" ||
    calculation?.calculation_type === "jetski_commercial";

  // בדיקה אם זה מחשבון רכב אספנות
  const isCollector = calculation?.calculation_type === "collector_vehicle";

  // בדיקה אם זה מחשבון אופנועים
  const isMotorcycle =
    calculation?.calculation_type === "motorcycle_vehicle" ||
    calculation?.calculation_type === "motorcycle";

  // בדיקה אם זה מחשבון אופנועים מסחרי
  const isMotorcycleCommercial =
    calculation?.calculation_type === "motorcycle_commercial";

  // בדיקה אם זה מחשבון M1 חשמלי מסחרי
  const isM1ElectricCommercial =
    calculation?.calculation_type === "M1_electric_commercial" ||
    calculation?.calculation_type === "m1_electric_commercial";

  // בדיקה אם זה מחשבון N1 & N2 חשמלי מסחרי
  const isN1N2ElectricCommercial =
    calculation?.calculation_type === "N1_N2_electric_commercial" ||
    calculation?.calculation_type === "n1_n2_electric_commercial";

  // בדיקה אם זה מחשבון N1 & N2 בנזין/דיזל מסחרי
  const isN1N2Commercial =
    calculation?.calculation_type === "N1N2_commercial" ||
    calculation?.calculation_type === "n1n2_commercial";

  // בדיקה אם זה מחשבון M1 בנזין/דיזל מסחרי
  const isM1Commercial =
    calculation?.calculation_type === "M1_vehicle_commercial" ||
    calculation?.calculation_type === "m1_commercial";

  // בדיקה אם זה מחשבון נגררים
  const isTrailer =
    calculation?.calculation_type === "trailer_vehicle" ||
    calculation?.calculation_type === "trailer";

  // ========== JSX ==========
  return (
    <div
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 print:bg-white print:py-2"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {/* Loading State */}
        {isLoading && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="text-gray-600">טוען חישוב...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-red-700 mb-4">{error}</p>
            <button
              onClick={handleGoBack}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              חזרה להיסטוריה
            </button>
          </div>
        )}

        {/* Main Content */}
        {calculation && data && !isLoading && !error && (
          <div className="space-y-6 print:block" ref={printRef}>
            {/* הערת שנת המס — ילד ראשון של printRef, מחוץ לכרטיס הכותרת שההדפסה מוחקת */}
            <TaxYearNotice
              text={resolveSavedTaxYearNotice(
                calculation.result_data,
                calculation.calculation_date
              )}
            />
            {/* Header */}
            <div className="bg-white rounded-lg shadow p-6 print:shadow-none print:p-4 print:block print:mb-4">
              <div className="flex items-center justify-between mb-4 print:mb-2">
                <h1 className="text-2xl font-bold text-gray-800 print:text-xl">
                  תוצאות חישוב - {data.meta.calculatorName}
                </h1>
                <div className="flex gap-2 print:hidden">
                  <button
                    onClick={handleGoBack}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
                  >
                    ← חזרה
                  </button>
                  <button
                    onClick={handlePrint}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                  >
                    הדפסה
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                נשמר בתאריך: {data.meta.calculationDate}
              </p>
            </div>

            {/* נתוני קלט */}
            <div className="bg-white rounded-lg shadow p-6 print:shadow-none print:p-4 print:block print:mb-4">
              <h2 className="text-lg font-bold text-gray-800 mb-4 border-b pb-2">
                נתוני הקלט שהוזנו:
              </h2>
              <table className="w-full text-sm">
                <tbody>
                  {/* תאריך ייצור - לא למוניות אישי (כן למוניות מסחרי) */}
                  {!isTaxi && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600 w-1/2">תאריך ייצור:</td>
                      <td className="py-2 font-medium text-left">
                        {data.input.manufacturingDate}
                      </td>
                    </tr>
                  )}
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">
                      {isJetski || isJetskiCommercial
                        ? "ערך אופנוע ים:"
                        : isTrailer
                        ? "ערך נגרר:"
                        : "ערך רכב:"}
                    </td>
                    <td className="py-2 font-medium text-left">
                      {data.input.vehiclePrice} {data.input.currency}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">מטבע:</td>
                    <td className="py-2 font-medium text-left">
                      {data.input.currencyName}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">שער חליפין:</td>
                    <td className="py-2 font-medium text-left">
                      {data.input.exchangeRate}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">עלות הובלה:</td>
                    <td className="py-2 font-medium text-left">
                      {data.input.shippingCost} {data.input.currency}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">עלות ביטוח:</td>
                    <td className="py-2 font-medium text-left">
                      {data.input.insuranceCost} {data.input.currency}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">שער חליפין ביטוח:</td>
                    <td className="py-2 font-medium text-left">
                      {data.input.insuranceExchangeRate}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">הוצאות מקומיות:</td>
                    <td className="py-2 font-medium text-left">
                      ₪{data.input.localExpenses}
                    </td>
                  </tr>
                  {/* סוג מנוע - למוניות (אישי ומסחרי) ולאופנועים */}
                  {(isTaxi || isTaxiCommercial) && data.input.engineType && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">סוג מנוע:</td>
                      <td className="py-2 font-medium text-left text-blue-600">
                        {data.input.engineType}
                      </td>
                    </tr>
                  )}
                  {(isMotorcycle || isMotorcycleCommercial) &&
                    data.input.engineTypeDisplay && (
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">סוג מנוע:</td>
                        <td className="py-2 font-medium text-left text-blue-600">
                          {data.input.engineTypeDisplay}
                        </td>
                      </tr>
                    )}
                  {/* הצהרת מקור - לא לנגררים */}
                  {!isTrailer && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">הצהרת מקור:</td>
                      <td className="py-2 font-medium text-left">
                        {data.input.originDeclarationText}
                      </td>
                    </tr>
                  )}
                  {/* משקל רכב - לא למוניות אישי, לא לאופנוע ים, לא לאופנועים (כן למוניות מסחרי) */}
                  {!isTaxi &&
                    !isJetski &&
                    !isJetskiCommercial &&
                    !isMotorcycle &&
                    !isMotorcycleCommercial && (
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          {isTrailer ? "משקל נגרר:" : "משקל רכב:"}
                        </td>
                        <td className="py-2 font-medium text-left">
                          {data.input.weightText} ✓
                        </td>
                      </tr>
                    )}
                  {/* ציון ירוק - רק אם יש ואם לא חשמלי ולא אופנוע ים ולא רכב אספנות ולא אופנועים */}
                  {data.input.greenRatingGroup &&
                    !calculation?.calculation_type
                      ?.toLowerCase()
                      .includes("electric") &&
                    !isJetski &&
                    !isCollector &&
                    !isMotorcycle && (
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">ציון ירוק:</td>
                        <td className="py-2 font-medium text-left">
                          {(data.input as any).greenRatingDisplayName
                            ? (data.input as any).greenRatingDisplayName
                            : `קבוצה ${data.input.greenRatingGroup}`}
                        </td>
                      </tr>
                    )}
                  {/* תאריך עליה וזמן הובלה - לא למוניות, לא לאופנוע ים, לא לרכב אספנות, לא לאופנועים, לא ל-M1 חשמלי מסחרי */}
                  {!isTaxi &&
                    !isJetski &&
                    !isJetskiCommercial &&
                    !isCollector &&
                    !isMotorcycle &&
                    !isMotorcycleCommercial &&
                    !isM1ElectricCommercial &&
                    !isN1N2ElectricCommercial &&
                    !isN1N2Commercial &&
                    !isM1Commercial &&
                    !isTrailer && (
                      <>
                        <tr className="border-b">
                          <td className="py-2 text-gray-600">
                            תאריך עליה לכביש:
                          </td>
                          <td className="py-2 font-medium text-left">
                            {data.input.roadEntryDate}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 text-gray-600">זמן הובלה:</td>
                          <td className="py-2 font-medium text-left">
                            {data.input.shippingTimeMonths === 0 ||
                            data.input.shippingTimeMonths === "0"
                              ? "הרכב כבר בארץ"
                              : `${data.input.shippingTimeMonths} חודשים`}
                          </td>
                        </tr>
                      </>
                    )}
                </tbody>
              </table>
            </div>

            {/* חישוב לפי המחיר */}
            <div className="bg-white rounded-lg shadow p-6 print:shadow-none print:p-4 print:block print:mb-4">
              {/* בלוק מחיר לצרכן - רק אם לא מונית (אישי ומסחרי), לא אופנוע ים, לא רכב אספנות, לא אופנועים */}
              {!isTaxi &&
                !isTaxiCommercial &&
                !isJetski &&
                !isJetskiCommercial &&
                !isCollector &&
                !isMotorcycle &&
                !isN1N2Commercial &&
                !isM1Commercial &&
                !isTrailer && (
                  <div className="border-2 border-yellow-400 rounded-lg p-4 mb-4 bg-yellow-50">
                    <h2 className="text-lg font-bold text-gray-800 mb-2">
                      {isM1ElectricCommercial ||
                      isN1N2ElectricCommercial ||
                      isN1N2Commercial
                        ? "תוצאות החישוב"
                        : "חישוב לפי המחיר המומלץ"}
                    </h2>
                    <p className="text-2xl font-bold text-gray-900">
                      מחיר לצרכן: ₪{data.results.consumerPrice}
                    </p>
                    {!isM1ElectricCommercial &&
                      !isN1N2ElectricCommercial &&
                      !isN1N2Commercial && (
                        <div className="mt-2 text-sm text-gray-600">
                          {data.results.luxuryTaxApplicable && (
                            <span className="ml-4">
                              מס יוקרה חל: {data.results.luxuryTaxRate}%
                            </span>
                          )}
                          <span>
                            חודשים על הכביש: {data.results.monthsOnRoad}
                          </span>
                        </div>
                      )}
                  </div>
                )}

              <h3 className="text-md font-bold text-gray-800 mb-3">
                פירוט החישוב:
              </h3>
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">ערך כולל לצורכי מכס:</td>
                    <td className="py-2 font-medium text-left">
                      ₪{data.results.taxValueIls}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">סך המכס:</td>
                    <td className="py-2 font-medium text-left">
                      {isJetski || isJetskiCommercial || isTrailer ? (
                        <span className="text-green-700">₪0 (פטור)</span>
                      ) : (
                        `₪${data.results.customsFee}`
                      )}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">שיעור המכס:</td>
                    <td className="py-2 font-medium text-left">
                      {data.results.customsRatePercent}%
                    </td>
                  </tr>
                  {/* מס קניה לפני התאמות - רק למוניות מסחרי */}
                  {isTaxiCommercial &&
                    data.results.purchaseTaxBeforeAdjustments && (
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          מס קניה לפני התאמות:
                        </td>
                        <td className="py-2 font-medium text-left">
                          ₪{data.results.purchaseTaxBeforeAdjustments}
                        </td>
                      </tr>
                    )}
                  {/* הנחת ציון ירוק - למוניות מסחרי */}
                  {isTaxiCommercial && data.results.greenRatingApplied && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">הנחת ציון ירוק:</td>
                      <td className="py-2 font-medium text-left text-green-700">
                        -₪
                        {formatNumber(
                          Math.abs(data.results.greenRatingAdjustment)
                        )}
                      </td>
                    </tr>
                  )}

                  {/* הנחת אבזור בטיחותי - למוניות מסחרי */}
                  {isTaxiCommercial &&
                    data.results.safetyDiscountAmount > 0 && (
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          הנחת אבזור בטיחותי:
                        </td>
                        <td className="py-2 font-medium text-left text-green-700">
                          -₪{formatNumber(data.results.safetyDiscountAmount)}
                        </td>
                      </tr>
                    )}

                  {/* === בלוק אופנועים מסחרי === */}
                  {isMotorcycleCommercial && (
                    <>
                      {/* מס קניה לפני הנחה */}
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          מס קניה לפני הנחה:
                        </td>
                        <td className="py-2 font-medium text-left">
                          ₪{data.results.purchaseTaxBeforeDiscount}
                        </td>
                      </tr>
                      {/* הנחת אבזור בטיחותי */}
                      {data.results.safetyDiscountAmount > 0 && (
                        <tr className="border-b">
                          <td className="py-2 text-gray-600">
                            הנחת אבזור בטיחותי:
                          </td>
                          <td className="py-2 font-medium text-left text-green-700">
                            -₪{formatNumber(data.results.safetyDiscountAmount)}
                          </td>
                        </tr>
                      )}
                      {/* מס קניה סופי */}
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          מס קניה סופי (אחרי הנחה):
                        </td>
                        <td className="py-2 font-medium text-left">
                          ₪{data.results.finalPurchaseTax}
                        </td>
                      </tr>
                      {/* שיעור מס הקניה המלא */}
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          שיעור מס הקניה המלא:
                        </td>
                        <td className="py-2 font-medium text-left">
                          {data.results.purchaseTaxRateNumber}%
                        </td>
                      </tr>
                    </>
                  )}
                  {/* === בלוק M1 בנזין/דיזל מסחרי === */}
                  {isM1Commercial && (
                    <>
                      {/* מס קניה בסיסי */}
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          מס קניה בסיסי (
                          {data.results.basePurchaseTaxRatePercent}%):
                        </td>
                        <td className="py-2 font-medium text-left">
                          ₪{data.results.basePurchaseTax}
                        </td>
                      </tr>
                      {/* ציון ירוק */}
                      {data.results.greenRatingApplied && (
                        <tr className="border-b">
                          <td className="py-2 text-gray-600">
                            ציון ירוק - {data.results.greenRatingDisplayName}:
                          </td>
                          <td
                            className={`py-2 font-medium text-left ${
                              data.results.greenRatingIsDiscount
                                ? "text-green-700"
                                : "text-red-700"
                            }`}
                          >
                            {data.results.greenRatingIsDiscount ? "-" : "+"}₪
                            {formatNumber(
                              Math.abs(data.results.greenRatingAdjustment)
                            )}
                          </td>
                        </tr>
                      )}
                      {/* אבזור בטיחותי */}
                      {data.results.safetyDiscount > 0 && (
                        <tr className="border-b">
                          <td className="py-2 text-gray-600">
                            אבזור בטיחותי (רמה {data.results.safetyLevel}):
                          </td>
                          <td className="py-2 font-medium text-left text-green-700">
                            -₪{formatNumber(data.results.safetyDiscount)}
                          </td>
                        </tr>
                      )}
                      {/* מס קניה אחרי הנחות */}
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          מס קניה אחרי הנחות:
                        </td>
                        <td className="py-2 font-medium text-left">
                          ₪{data.results.purchaseTaxAfterDiscounts}
                        </td>
                      </tr>
                      {/* מס יוקרה */}
                      {data.results.hasLuxuryTax && (
                        <>
                          <tr className="border-b bg-orange-50">
                            <td className="py-2 text-gray-600" colSpan={2}>
                              <span className="text-orange-600 font-medium">
                                ✓ מס יוקרה חל - מחיר מעל 300,000 ש"ח
                              </span>
                            </td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2 text-gray-600">
                              שיעור מס יוקרה:
                            </td>
                            <td className="py-2 font-medium text-left">
                              {data.results.luxuryTaxRate}%
                            </td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2 text-gray-600">סך מס יוקרה:</td>
                            <td className="py-2 font-medium text-left">
                              ₪{formatNumber(data.results.luxuryTaxAmount)}
                            </td>
                          </tr>
                        </>
                      )}
                    </>
                  )}
                  {/* === בלוק N1N2 בנזין/דיזל מסחרי === */}
                  {isN1N2Commercial && (
                    <>
                      {/* מס קניה בסיסי */}
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          מס קניה בסיסי (
                          {data.results.basePurchaseTaxRatePercent}%):
                        </td>
                        <td className="py-2 font-medium text-left">
                          ₪{data.results.basePurchaseTax}
                        </td>
                      </tr>
                      {/* ציון ירוק */}
                      {data.results.greenRatingApplied && (
                        <tr className="border-b">
                          <td className="py-2 text-gray-600">
                            ציון ירוק - {data.results.greenRatingDisplayName}:
                          </td>
                          <td className="py-2 font-medium text-left text-green-700">
                            -₪
                            {formatNumber(
                              Math.abs(data.results.greenRatingAdjustment)
                            )}
                          </td>
                        </tr>
                      )}
                      {/* אבזור בטיחותי */}
                      {data.results.safetyDiscount > 0 && (
                        <tr className="border-b">
                          <td className="py-2 text-gray-600">
                            אבזור בטיחותי (רמה {data.results.safetyLevel}):
                          </td>
                          <td className="py-2 font-medium text-left text-green-700">
                            -₪{formatNumber(data.results.safetyDiscount)}
                          </td>
                        </tr>
                      )}
                      {/* מס קניה אחרי הנחות */}
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          מס קניה אחרי הנחות:
                        </td>
                        <td className="py-2 font-medium text-left">
                          ₪{data.results.purchaseTaxAfterDiscounts}
                        </td>
                      </tr>
                      {/* מס יוקרה */}
                      {data.results.hasLuxuryTax && (
                        <>
                          <tr className="border-b bg-orange-50">
                            <td className="py-2 text-gray-600" colSpan={2}>
                              <span className="text-orange-600 font-medium">
                                ✓ מס יוקרה חל - מחיר מעל 300,000 ש"ח
                              </span>
                            </td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2 text-gray-600">
                              שיעור מס יוקרה:
                            </td>
                            <td className="py-2 font-medium text-left">
                              {data.results.luxuryTaxRate}%
                            </td>
                          </tr>
                          <tr className="border-b">
                            <td className="py-2 text-gray-600">סך מס יוקרה:</td>
                            <td className="py-2 font-medium text-left">
                              ₪{formatNumber(data.results.luxuryTaxAmount)}
                            </td>
                          </tr>
                        </>
                      )}
                    </>
                  )}
                  {/* ציון ירוק / הנחת חשמלי - רק ל-M1 חשמלי מסחרי */}
                  {(isM1ElectricCommercial || isN1N2ElectricCommercial) &&
                    data.results.greenDiscountApplied > 0 && (
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          ציון ירוק - {data.results.greenRatingDisplayName}:
                        </td>
                        <td className="py-2 font-medium text-left text-green-700">
                          -₪{formatNumber(data.results.greenDiscountApplied)}
                        </td>
                      </tr>
                    )}
                  {/* אבזור בטיחותי - רק ל-M1 חשמלי מסחרי */}
                  {(isM1ElectricCommercial || isN1N2ElectricCommercial) &&
                    data.results.safetyDiscount > 0 && (
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          אבזור בטיחותי (רמה {data.results.safetyLevel}):
                        </td>
                        <td className="py-2 font-medium text-left text-green-700">
                          -₪{formatNumber(data.results.safetyDiscount)}
                        </td>
                      </tr>
                    )}
                  {/* מס יוקרה - רק ל-M1 חשמלי מסחרי */}
                  {(isM1ElectricCommercial || isN1N2ElectricCommercial) &&
                    data.results.hasLuxuryTax && (
                      <>
                        <tr className="border-b bg-orange-50">
                          <td className="py-2 text-gray-600" colSpan={2}>
                            <span className="text-orange-600 font-medium">
                              ✓ מס יוקרה חל - מחיר מעל 300,000 ש"ח
                            </span>
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 text-gray-600">
                            שיעור מס יוקרה:
                          </td>
                          <td className="py-2 font-medium text-left">
                            {data.results.luxuryTaxRate}%
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 text-gray-600">סך מס יוקרה:</td>
                          <td className="py-2 font-medium text-left">
                            ₪{formatNumber(data.results.luxuryTaxAmount)}
                          </td>
                        </tr>
                      </>
                    )}
                  {/* בסיס למס קניה - רק לאופנוע ים */}
                  {(isJetski || isJetskiCommercial) && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">בסיס למס קניה:</td>
                      <td className="py-2 font-medium text-left">
                        ₪{data.results.purchaseTaxBase}
                      </td>
                    </tr>
                  )}
                  {/* סך מס קניה - לא לאופנועים מסחרי (כבר מוצג בבלוק שלו) */}
                  {!isMotorcycleCommercial && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">
                        {isM1ElectricCommercial ||
                        isN1N2ElectricCommercial ||
                        isN1N2Commercial ||
                        isM1Commercial
                          ? "מס קניה סופי:"
                          : "סך מס קניה:"}
                      </td>
                      <td className="py-2 font-medium text-left">
                        {isTrailer ? (
                          <span className="text-green-700">₪0 (פטור)</span>
                        ) : (
                          `₪${data.results.finalPurchaseTax}`
                        )}
                      </td>
                    </tr>
                  )}
                  {!isM1ElectricCommercial &&
                    !isN1N2ElectricCommercial &&
                    !isM1Commercial &&
                    !isMotorcycleCommercial && (
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">שיעור מס קניה:</td>
                        <td className="py-2 font-medium text-left">
                          {isTaxi || isTaxiCommercial
                            ? "8"
                            : isJetski || isJetskiCommercial
                            ? "20"
                            : isCollector
                            ? "72"
                            : isMotorcycle
                            ? data.results.purchaseTaxRateNumber
                            : isTrailer
                            ? "0"
                            : data.results.finalPurchaseTaxRate}
                          %
                          {!isTaxi &&
                            !isJetski &&
                            data.results.luxuryTaxApplicable && (
                              <span className="text-gray-500 text-xs mr-1">
                                (כולל {data.results.luxuryTaxRate}% מס יוקרה)
                              </span>
                            )}
                        </td>
                      </tr>
                    )}
                  {/* שיעור מס קניה אפקטיבי - רק אם לא מונית (אישי ומסחרי), לא אופנוע ים, לא רכב אספנות, לא אופנועים */}
                  {!isTaxi &&
                    !isTaxiCommercial &&
                    !isJetski &&
                    !isJetskiCommercial &&
                    !isCollector &&
                    !isMotorcycle &&
                    !isMotorcycleCommercial &&
                    !isTrailer && (
                      <tr className="border-b">
                        <td className="py-2 text-gray-600">
                          שיעור מס קניה אפקטיבי:
                        </td>
                        <td className="py-2 font-medium text-left">
                          {data.results.effectivePurchaseTaxRate}%
                        </td>
                      </tr>
                    )}
                  {/* תוספת ציון ירוק - רק למוניות */}
                  {isTaxi && data.results.greenRatingApplied && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">
                        {data.results.greenRatingIsDiscount
                          ? "הנחת ציון ירוק:"
                          : "תוספת ציון ירוק:"}
                      </td>
                      <td className="py-2 font-medium text-left">
                        <span
                          className={
                            data.results.greenRatingIsDiscount
                              ? "text-green-700"
                              : "text-red-700"
                          }
                        >
                          {data.results.greenRatingIsDiscount ? "-" : "+"}₪
                          {formatNumber(
                            Math.abs(data.results.greenRatingAdjustment)
                          )}
                        </span>
                        {data.results.greenRatingDisplayName && (
                          <span className="text-gray-500 text-xs mr-2">
                            (קבוצה {data.results.greenRatingDisplayName})
                          </span>
                        )}
                      </td>
                    </tr>
                  )}
                  {/* מס יוקרה - לא למוניות ולא לאופנוע ים */}
                  {!isTaxi && !isJetski && data.results.luxuryTaxApplicable && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">שיעור מס יוקרה:</td>
                      <td className="py-2 font-medium text-left">
                        {data.results.luxuryTaxRate}%
                      </td>
                    </tr>
                  )}
                  {/* בסיס למע"מ - רק לאופנוע ים */}
                  {isJetski && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">בסיס למע"מ:</td>
                      <td className="py-2 font-medium text-left">
                        ₪{data.results.vatBase}
                      </td>
                    </tr>
                  )}
                  {/* בסיס למע"מ - לאופנוע ים ול-M1/N1N2 מסחרי */}
                  {(isJetski ||
                    isJetskiCommercial ||
                    isM1ElectricCommercial ||
                    isN1N2ElectricCommercial ||
                    isN1N2Commercial ||
                    isM1Commercial) && (
                    <tr className="border-b">
                      <td className="py-2 text-gray-600">בסיס למע"מ:</td>
                      <td className="py-2 font-medium text-left">
                        ₪{data.results.vatBase}
                      </td>
                    </tr>
                  )}
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">סך המע"מ:</td>
                    <td className="py-2 font-medium text-left">
                      ₪{data.results.vatAmount}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600">שיעור המע"מ:</td>
                    <td className="py-2 font-medium text-left">
                      {data.results.vatRate}%
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600 font-bold">סך המיסים:</td>
                    <td className="py-2 font-bold text-left text-red-700">
                      ₪
                      {isTaxi ||
                      isTaxiCommercial ||
                      isJetski ||
                      isJetskiCommercial ||
                      isCollector ||
                      isMotorcycle ||
                      isMotorcycleCommercial ||
                      isM1ElectricCommercial ||
                      isN1N2ElectricCommercial ||
                      isN1N2Commercial ||
                      isM1Commercial ||
                      isTrailer
                        ? data.results.totalTaxes
                        : formatNumber(calculateTotalTaxes(calculation))}
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-600 font-bold">
                      {isM1ElectricCommercial ||
                      isN1N2ElectricCommercial ||
                      isN1N2Commercial ||
                      isM1Commercial
                        ? "שיעור מיסוי כולל:"
                        : "שיעור המיסים:"}
                    </td>
                    <td className="py-2 font-bold text-left text-red-700">
                      {isTaxi ||
                      isTaxiCommercial ||
                      isJetski ||
                      isJetskiCommercial ||
                      isCollector ||
                      isMotorcycle ||
                      isMotorcycleCommercial ||
                      isM1ElectricCommercial ||
                      isN1N2ElectricCommercial ||
                      isN1N2Commercial ||
                      isM1Commercial ||
                      isTrailer
                        ? data.results.taxRatePercent
                        : calculateTaxRate(calculation).toFixed(1)}
                      %
                    </td>
                  </tr>
                  <tr className="bg-gray-100">
                    <td className="py-3 font-bold text-gray-800">
                      {isJetski || isJetskiCommercial
                        ? "עלות אופנוע הים כולל מיסים:"
                        : isMotorcycle || isMotorcycleCommercial
                        ? "עלות האופנוע כולל מיסים:"
                        : isTrailer
                        ? "עלות הנגרר כולל מיסים:"
                        : "עלות הרכב כולל מיסים:"}
                    </td>
                    <td className="py-3 font-bold text-left text-lg text-red-700">
                      ₪{data.results.totalCost}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* אבזור בטיחותי - אופנועים */}
            {isMotorcycle && data.safety.hasData && (
              <div className="bg-white rounded-lg shadow p-6 print:shadow-none print:p-4 print:block print:mb-4">
                <h2 className="text-lg font-bold text-gray-800 mb-2 border-b pb-2">
                  💎 הנחה על אבזור בטיחותי
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  סמן את האבזור הבטיחותי הקיים באופנוע שלך לחישוב הנחה אפשרית:
                </p>

                {/* רשימת מערכות בטיחות */}
                <div className="space-y-2 mb-4">
                  {Object.entries(SAFETY_SYSTEMS_MOTORCYCLE).map(
                    ([key, system]) => (
                      <div
                        key={key}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span
                          className={
                            data.safety.features[key]
                              ? "text-green-700"
                              : "text-gray-400"
                          }
                        >
                          {data.safety.features[key] ? "☑" : "☐"}
                        </span>
                        <span
                          className={
                            data.safety.features[key]
                              ? "font-medium"
                              : "text-gray-500"
                          }
                        >
                          {system.name}
                        </span>
                      </div>
                    )
                  )}
                </div>

                {/* תוצאות */}
                {data.safety.calculatedDiscount > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600"> הנחה אפשרית:</span>
                      <span className="font-bold text-green-700">
                        ₪{formatNumber(data.safety.calculatedDiscount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">עלות סופית עם הנחה:</span>
                      <span className="font-bold text-blue-600">
                        ₪{data.results.finalTotalWithDiscount}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* אבזור בטיחותי - אופנועים מסחרי */}
            {isMotorcycleCommercial && data.safety.hasData && (
              <div className="bg-white rounded-lg shadow p-6 print:shadow-none print:p-4 print:block print:mb-4">
                <h2 className="text-lg font-bold text-gray-800 mb-2 border-b pb-2">
                  💎 פירוט אבזור בטיחותי
                </h2>

                {/* רשימת מערכות בטיחות */}
                <div className="space-y-2 mb-4">
                  {Object.entries(SAFETY_SYSTEMS_MOTORCYCLE).map(
                    ([key, system]) => (
                      <div
                        key={key}
                        className="flex items-center gap-2 text-sm"
                      >
                        <span
                          className={
                            data.safety.features[key]
                              ? "text-green-700"
                              : "text-gray-400"
                          }
                        >
                          {data.safety.features[key] ? "✅" : "☐"}
                        </span>
                        <span
                          className={
                            data.safety.features[key]
                              ? "font-medium"
                              : "text-gray-500"
                          }
                        >
                          {system.name}
                        </span>
                      </div>
                    )
                  )}
                </div>

                {/* סיכום הנחה */}
                {data.safety.discountAmount > 0 && (
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">
                        הנחה שהתקבלה:
                      </span>
                      <span className="font-bold text-green-700 text-lg">
                        ₪{formatNumber(data.safety.discountAmount)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* אבזור בטיחותי - M1/N1 (לא אופנועים) */}
            {!isMotorcycle &&
              !isMotorcycleCommercial &&
              data.safety.hasData && (
                <div className="bg-white rounded-lg shadow p-6 print:shadow-none print:p-4 print:block print:mb-4">
                  <h2 className="text-lg font-bold text-gray-800 mb-2 border-b pb-2">
                    מחשבון רמת אבזור בטיחותי (נלווה)
                  </h2>
                  <p className="text-sm text-gray-600 mb-4">
                    חישוב רמת אבזור בטיחותי על פי נוהל 03/13 משרד התחבורה -
                    ייתכן ותהיה זכאי להחזר לאחר הטסט
                  </p>
                  <p className="text-sm text-blue-600 mb-4">
                    שאל את עמיל המכס שלך לפרטים נוספים
                  </p>

                  {/* כריות אוויר */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-1">
                      כמה כריות אויר יש ברכב שלך? (נדרש מינימום{" "}
                      {data.safety.minAirbags || 6} עבור{" "}
                      {data.safety.vehicleType || "M1"})
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg">
                        {data.safety.airbagsCount}
                      </span>
                      <span className="text-sm text-gray-500">
                        כריות (נדרש מינימום {data.safety.minAirbags || 6} עבור{" "}
                        {data.safety.vehicleType || "M1"})
                      </span>
                    </div>
                  </div>

                  {/* מערכות בטיחות */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      מערכות בטיחות:
                    </p>
                    <div className="space-y-1">
                      {getActiveSafetySystems(
                        data.safety.systems,
                        SAFETY_SYSTEMS_M1
                      ).map((system) => (
                        <div
                          key={system.key}
                          className="flex items-center gap-2 text-sm"
                        >
                          <span className="text-green-700">✓</span>
                          <span>
                            {system.name} ({system.points})
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* תוצאות אבזור */}
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      תוצאות חישוב אבזור בטיחותי:
                    </p>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr className="border-b">
                          <td className="py-2 text-gray-600">ניקוד כולל:</td>
                          <td className="py-2 font-bold text-left text-blue-600">
                            {data.safety.safetyScore}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="py-2 text-gray-600">רמת אבזור:</td>
                          <td className="py-2 font-bold text-left text-blue-600">
                            {data.safety.safetyLevel}
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 text-gray-600">סכום הנחה:</td>
                          <td className="py-2 font-bold text-left text-green-700">
                            ₪{data.safety.safetyDiscount}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 text-xs text-gray-500 space-y-1">
                    <p>
                      * ייתכן ותהיה זכאי לאחר הטסט להחזר בגין אבזור בטיחותי (שאל
                      את עמיל המכס שלך)
                    </p>
                    <p>* חישוב נלווה בלבד - לא משפיע על עלות הייבוא הסופית</p>
                  </div>
                </div>
              )}
            {/* דייק לי את התוצאה - רק אם יש נתונים */}
            {data.actualCost?.hasData && data.actualCost.calculatedResult && (
              <div className="bg-white rounded-lg shadow p-6 print:shadow-none print:p-4 print:block print:mb-4 border-2 border-blue-200">
                {" "}
                <h2 className="text-lg font-bold text-blue-800 mb-4">
                  🎯 דייק לי את התוצאה - עלות הרכב בפועל
                </h2>
                {/* שערי מט"ח בפועל */}
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h3 className="font-bold text-blue-700 mb-2">
                    💱 שערי מט"ח בפועל
                  </h3>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-blue-100">
                        <td className="py-2 text-gray-600">שער רכב בפועל:</td>
                        <td className="py-2 font-medium text-left">
                          {data.actualCost.actualVehicleRate}
                        </td>
                      </tr>
                      <tr className="border-b border-blue-100">
                        <td className="py-2 text-gray-600">שער הובלה בפועל:</td>
                        <td className="py-2 font-medium text-left">
                          {data.actualCost.actualShippingRate}
                        </td>
                      </tr>
                      {data.actualCost.actualInsuranceRate &&
                        parseFloat(data.actualCost.actualInsuranceRate) > 0 && (
                          <tr className="border-b border-blue-100">
                            <td className="py-2 text-gray-600">
                              שער ביטוח בפועל:
                            </td>
                            <td className="py-2 font-medium text-left">
                              {data.actualCost.actualInsuranceRate}
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
                {/* עלויות נלוות */}
                {(parseFloat(data.actualCost.customsBrokerCost || "0") > 0 ||
                  parseFloat(data.actualCost.bankFees || "0") > 0 ||
                  parseFloat(data.actualCost.storageCost || "0") > 0 ||
                  parseFloat(data.actualCost.unloadingCost || "0") > 0 ||
                  parseFloat(data.actualCost.brokerFee || "0") > 0 ||
                  parseFloat(data.actualCost.localTransportCost || "0") > 0 ||
                  parseFloat(data.actualCost.testAndLicenseCost || "0") >
                    0) && (
                  <div className="bg-blue-50 p-4 rounded-lg mb-4">
                    <h3 className="font-bold text-blue-700 mb-2">
                      עלויות נלוות
                    </h3>
                    <table className="w-full text-sm">
                      <tbody>
                        {parseFloat(data.actualCost.customsBrokerCost || "0") >
                          0 && (
                          <tr className="border-b border-blue-100">
                            <td className="py-2 text-gray-600">
                              עלות עמילות מכס:
                            </td>
                            <td className="py-2 font-medium text-left">
                              ₪{formatNumber(data.actualCost.customsBrokerCost)}
                            </td>
                          </tr>
                        )}
                        {parseFloat(data.actualCost.bankFees || "0") > 0 && (
                          <tr className="border-b border-blue-100">
                            <td className="py-2 text-gray-600">עמלות בנק:</td>
                            <td className="py-2 font-medium text-left">
                              ₪{formatNumber(data.actualCost.bankFees)}
                            </td>
                          </tr>
                        )}
                        {parseFloat(data.actualCost.storageCost || "0") > 0 && (
                          <tr className="border-b border-blue-100">
                            <td className="py-2 text-gray-600">עלות אחסנה:</td>
                            <td className="py-2 font-medium text-left">
                              ₪{formatNumber(data.actualCost.storageCost)}
                            </td>
                          </tr>
                        )}
                        {parseFloat(data.actualCost.unloadingCost || "0") >
                          0 && (
                          <tr className="border-b border-blue-100">
                            <td className="py-2 text-gray-600">עלות ריקון:</td>
                            <td className="py-2 font-medium text-left">
                              ₪{formatNumber(data.actualCost.unloadingCost)}
                            </td>
                          </tr>
                        )}
                        {parseFloat(data.actualCost.brokerFee || "0") > 0 && (
                          <tr className="border-b border-blue-100">
                            <td className="py-2 text-gray-600">דמי מתווך:</td>
                            <td className="py-2 font-medium text-left">
                              ₪{formatNumber(data.actualCost.brokerFee)}
                            </td>
                          </tr>
                        )}
                        {parseFloat(data.actualCost.localTransportCost || "0") >
                          0 && (
                          <tr className="border-b border-blue-100">
                            <td className="py-2 text-gray-600">
                              הובלה לאחר שחרור:
                            </td>
                            <td className="py-2 font-medium text-left">
                              ₪
                              {formatNumber(data.actualCost.localTransportCost)}
                            </td>
                          </tr>
                        )}
                        {parseFloat(data.actualCost.testAndLicenseCost || "0") >
                          0 && (
                          <tr className="border-b border-blue-100">
                            <td className="py-2 text-gray-600">טסט ורישוי:</td>
                            <td className="py-2 font-medium text-left">
                              ₪
                              {formatNumber(data.actualCost.testAndLicenseCost)}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                {/* סיכום עלות בפועל */}
                <div className="bg-blue-100 p-4 rounded-lg">
                  <h3 className="font-bold text-blue-800 mb-2">
                    📊 סיכום עלות בפועל
                  </h3>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-blue-200">
                        <td className="py-2 text-gray-600">
                          ערך הרכב בש"ח (לפי שער בפועל):
                        </td>
                        <td className="py-2 font-medium text-left">
                          ₪
                          {formatNumber(
                            data.actualCost.calculatedResult.vehicleValueIls
                          )}
                        </td>
                      </tr>
                      <tr className="border-b border-blue-200">
                        <td className="py-2 text-gray-600">ערך ההובלה בש"ח:</td>
                        <td className="py-2 font-medium text-left">
                          ₪
                          {formatNumber(
                            data.actualCost.calculatedResult.shippingValueIls
                          )}
                        </td>
                      </tr>
                      {data.actualCost.calculatedResult.insuranceValueIls >
                        0 && (
                        <tr className="border-b border-blue-200">
                          <td className="py-2 text-gray-600">
                            ערך הביטוח בש"ח:
                          </td>
                          <td className="py-2 font-medium text-left">
                            ₪
                            {formatNumber(
                              data.actualCost.calculatedResult.insuranceValueIls
                            )}
                          </td>
                        </tr>
                      )}
                      <tr className="border-b border-blue-200">
                        <td className="py-2 text-gray-600">הוצאות מקומיות:</td>
                        <td className="py-2 font-medium text-left">
                          ₪
                          {formatNumber(
                            data.actualCost.calculatedResult.localExpenses
                          )}
                        </td>
                      </tr>
                      <tr className="border-b border-blue-200">
                        <td className="py-2 text-gray-600">סך המיסים:</td>
                        <td className="py-2 font-medium text-left">
                          ₪
                          {formatNumber(
                            data.actualCost.calculatedResult.totalTaxes
                          )}
                        </td>
                      </tr>
                      {data.actualCost.calculatedResult.additionalCosts > 0 && (
                        <tr className="border-b border-blue-200">
                          <td className="py-2 text-gray-600">עלויות נלוות:</td>
                          <td className="py-2 font-medium text-left">
                            ₪
                            {formatNumber(
                              data.actualCost.calculatedResult.additionalCosts
                            )}
                          </td>
                        </tr>
                      )}
                      <tr className="bg-blue-200">
                        <td className="py-3 font-bold text-blue-900">
                          סה"כ עלות הרכב בפועל:
                        </td>
                        <td className="py-3 font-bold text-left text-lg text-blue-900">
                          ₪
                          {formatNumber(
                            data.actualCost.calculatedResult.totalActualCost
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* הערות */}
            {data.meta.notes && (
              <div className="bg-white rounded-lg shadow p-6 print:shadow-none print:p-4 print:block print:mb-4">
                <h2 className="text-lg font-bold text-gray-800 mb-3">הערות:</h2>
                <div className="bg-gray-50 rounded p-4 text-gray-700 whitespace-pre-wrap">
                  {data.meta.notes}
                </div>
              </div>
            )}

            {/* כפתורים תחתונים */}
            <div className="flex justify-center gap-4 pt-4 print:hidden">
              <button
                onClick={handleGoBack}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                ← חזרה להיסטוריה
              </button>
              <button
                onClick={handlePrint}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                הדפסה
              </button>
            </div>

            {/* Footer להדפסה */}
            <div className="hidden print:block text-center text-xs text-gray-500 mt-8 pt-4 border-t">
              <p>השימוש במחשבון כפוף לתקנות המכס הישראליות</p>
              <p>CarTax - חישוב מס מדויק ומהיר לייבוא אישי</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculationResultViewPage;
